/** All UI texts (German, see Q1 in docs/HANDOVER.md). Keep every visible string here so the language can be switched. */
import type { BodyPart, Exercise, ExerciseError, Measure } from "../core/exercises";
import type { Placement } from "../core/motion";
import { RECORD_UNITS, type MetricKey, type Rec, type RecordKind, type Unit } from "../core/progress";
import type { SetValues } from "../core/sessions";
import type { SetType } from "../core/templates";
import type { ReadError } from "../core/transfer";

const equipment: Record<string, string> = {
  barbell: "Langhantel",
  dumbbell: "Kurzhantel",
  kettlebell: "Kettlebell",
  sandbag: "Sandbag",
  sled: "Schlitten",
  machine: "Maschine",
  bodyweight: "Körpergewicht",
  other: "Sonstiges",
};

const bodyPart: Record<BodyPart, string> = {
  chest: "Brust",
  back: "Rücken",
  legs: "Beine",
  shoulders: "Schultern",
  arms: "Arme",
  core: "Core",
  fullBody: "Ganzkörper",
  cardio: "Cardio",
};

const measure: Record<Measure, string> = {
  reps: "Wiederholungen",
  timed: "Reps in Zeit",
  hold: "Halten",
  distance: "Strecke",
};

const measureHint: Record<Measure, string> = {
  reps: "Gewicht × Wiederholungen (Körpergewicht: nur Wiederholungen)",
  timed: "So viele Wiederholungen wie möglich in einer Zeit (AMRAP)",
  hold: "Nur die Zeit, z. B. Plank",
  distance: "Meter, die Zeit wird mitgeschrieben",
};

const exerciseError: Record<ExerciseError, string> = {
  nameMissing: "Bitte einen Namen eingeben.",
  nameTaken: "Diesen Namen gibt es schon.",
  restInvalid: "Pause zwischen 0 und 10 Minuten.",
  equipmentMissing: "Bitte die Ausrüstung angeben.",
};

export const t = {
  appName: "Workout Helper",
  loading: "Lädt …",
  storageError: "Die Daten konnten nicht geladen werden:",
  version: (v: string) => `Version ${v}`,
  back: "‹ Zurück",
  cancel: "Abbrechen",
  save: "Speichern",

  tabs: { history: "Verlauf", workout: "Workout", exercises: "Übungen" },
  historyEmpty: "Hier erscheinen deine abgeschlossenen Workouts.",

  history: {
    title: "Verlauf",
    month: (year: number, month: number) => new Date(year, month, 1).toLocaleDateString("de-DE", { month: "long", year: "numeric" }),
    workouts: (n: number) => (n === 1 ? "1 Workout" : `${n} Workouts`),
    exerciseLine: (sets: number, name: string) => `${sets} × ${name}`,
    bestSet: "Bester Satz",
    prCount: (n: number) => (n === 1 ? "1 Rekord" : `${n} Rekorde`),
    edit: "Bearbeiten",
    saveAsTemplate: "Als Vorlage speichern",
    repeat: "Wiederholen",
    delete: "Löschen",
    confirmDelete: (name: string) => `Workout „${name}“ vom Verlauf löschen? Rekorde und Diagramme werden neu berechnet.`,
    templateSaved: (name: string) => `Vorlage „${name}“ gespeichert. Du findest sie im Tab Workout.`,
    workoutRunning: "Es läuft schon ein Workout. Beende es zuerst.",
    start: "Beginn",
    durationMin: "Dauer (Min.)",
    titleEdit: "Workout bearbeiten",
    timeInvalid: "Beginn und Dauer prüfen.",
    valueMissing: "Bei den rot markierten Sätzen fehlt der Wert (oder den Satz entfernen).",
    valueInvalid: "Einige Werte sind ungültig (rot markiert).",
    empty: "Ein Workout ohne Sätze kann nicht gespeichert werden. Lösche es stattdessen.",
  },
  progress: {
    metric: {
      e1rm: "Geschätztes 1RM", maxWeight: "Max. Gewicht", volume: "Volumen", maxReps: "Max. Reps", totalReps: "Reps gesamt",
      timedReps: "Reps pro Satz", timedTotal: "Reps gesamt", longestHold: "Längste Zeit", distance: "Längste Strecke",
      totalDistance: "Strecke gesamt", pace: "Tempo",
    } as Record<MetricKey, string>,
    record: {
      e1rm: "Geschätztes 1RM", maxWeight: "Max. Gewicht", setVolume: "Max. Volumen (Satz)", sessionVolume: "Max. Volumen (Workout)",
      repsAtWeight: "Reps", maxReps: "Max. Reps (Satz)", sessionReps: "Max. Reps (Workout)", timedReps: "Reps", longestHold: "Längste Zeit",
      longestDistance: "Längste Strecke",
    } as Record<RecordKind, string>,
    badge: {
      e1rm: "1RM", maxWeight: "Gewicht", setVolume: "Satz-Volumen", sessionVolume: "Volumen", repsAtWeight: "Reps",
      maxReps: "Reps", sessionReps: "Reps gesamt", timedReps: "Reps", longestHold: "Zeit", longestDistance: "Strecke",
    } as Record<RecordKind, string>,
    repsAt: (weight: string) => `Beste Reps mit ${weight}`,
    repsIn: (time: string) => `Meiste Reps in ${time}`,
    inTime: (time: string) => `in ${time}`,
    sinceLast: (delta: string) => `${delta} ggü. letztem Mal`,
    sinceBest: (delta: string) => `${delta} ggü. Bestwert`,
    newBest: "Neuer Bestwert",
    sameAsBest: "Bestwert erreicht",
    first: "Erstes Mal",
    sameAsLast: "wie letztes Mal",
    none: "Noch nicht gemacht. Hier erscheint jedes Workout mit dieser Übung.",
    noRecords: "Noch keine Rekorde. Sie entstehen mit dem ersten Workout mit dieser Übung (Aufwärmsätze zählen nicht).",
    noPoints: "Für diesen Wert gibt es noch keine Daten.",
    fewPoints: "Ab zwei Workouts gibt es hier einen Verlauf.",
    duration: "Satzdauer",
    lastTime: (date: string, set: string) => `Zuletzt ${date}: ${set}`,
    newRecords: (n: number) => (n === 1 ? "1 neuer Rekord" : `${n} neue Rekorde`),
    chartLabel: (metric: string) => `Diagramm: ${metric} über die Zeit`,
    pointLabel: (date: string, value: string) => `${date}: ${value}`,
  },

  workout: {
    title: "Workout",
    startEmpty: "Leeres Workout starten",
    templates: "Vorlagen",
    addTemplate: "+ Vorlage",
    noTemplates: "Noch keine Vorlage. Lege eine an, um ein Workout mit einem Tipp zu starten.",
    lastDone: (date: string) => `Zuletzt: ${date}`,
    neverDone: "Noch nie gemacht",
    moreExercises: (n: number) => `+ ${n} weitere`,
    start: "Workout starten",
    edit: "Bearbeiten",
    duplicate: "Duplizieren",
    delete: "Löschen",
    confirmDelete: (name: string) => `Vorlage „${name}“ löschen? Abgeschlossene Workouts bleiben im Verlauf.`,
    data: "Daten",
  },
  data: {
    title: "Daten",
    libraryTitle: "Übungen und Vorlagen",
    libraryText: "Als Datei speichern oder aus einer Datei übernehmen. Beim Import kommt Neues dazu; bei geänderten Einträgen wählst du, welche Fassung bleibt.",
    export: "Exportieren",
    import: "Importieren",
    backupTitle: "Komplettes Backup",
    backupText: "Übungen, Vorlagen und alle Workouts, z. B. für ein neues Handy. Wiederherstellen ersetzt alles auf diesem Gerät.",
    saveBackup: "Backup speichern",
    restore: "Backup wiederherstellen",
    workoutRunning: "Während ein Workout läuft, kann kein Backup wiederhergestellt werden.",
    saved: (name: string) => `Gespeichert: ${name}`,
    notSaved: "Nicht gespeichert.",
    saveFailed: (msg: string) => `Speichern fehlgeschlagen: ${msg}`,
    readError: {
      notJson: "Diese Datei ist keine Workout-Helper-Datei (kein JSON).",
      notOurs: "Diese Datei ist keine Workout-Helper-Datei.",
      newerVersion: "Diese Datei stammt aus einer neueren Version der App. Bitte die App aktualisieren.",
      damaged: "Die Datei ist beschädigt oder unvollständig. Es wurde nichts geändert.",
    } as Record<ReadError, string>,
    notBackup: "Das ist kein komplettes Backup, sondern eine Datei mit Übungen und Vorlagen. Nimm dafür „Importieren“.",
    nothingNew: "Alles in der Datei ist schon so auf diesem Gerät. Es wurde nichts geändert.",
    exercisesCount: (n: number) => (n === 1 ? "1 Übung" : `${n} Übungen`),
    templatesCount: (n: number) => (n === 1 ? "1 Vorlage" : `${n} Vorlagen`),
    sessionsCount: (n: number) => (n === 1 ? "1 Workout" : `${n} Workouts`),
    reviewTitle: "Import prüfen",
    newEntries: (list: string) => `Neu: ${list}.`,
    sameEntries: (n: number) => `${n} unverändert.`,
    matchedEntries: (names: string) => `Schon hier unter gleichem Namen, die Vorlagen nehmen diese: ${names}.`,
    conflictsHead: (n: number) => (n === 1 ? "1 Eintrag ist geändert:" : `${n} Einträge sind geändert:`),
    exerciseKind: "Übung",
    templateKind: "Vorlage",
    changed: (fields: string) => `Geändert: ${fields}`,
    field: {
      name: "Name", equipment: "Ausrüstung", bodyPart: "Körperteil", measure: "Messung", restSec: "Pause", notes: "Notizen",
      exercises: "Übungen und Sätze",
    } as Record<string, string>,
    keepMine: "Meine behalten",
    takeImport: "Import nehmen",
    allMine: "Alle: meine",
    allImport: "Alle: Import",
    doImport: "Importieren",
    importWord: "Import",
    imported: (parts: string) => `Importiert: ${parts}.`,
    addedPart: (what: string) => `${what} neu`,
    replacedPart: (what: string) => `${what} ersetzt`,
    renamed: (from: string, to: string) => `„${from}“ hieß schon eine andere Übung und heißt jetzt „${to}“.`,
    confirmRestore: (date: string, contents: string) =>
      `Backup vom ${date} wiederherstellen (${contents})? Alle Übungen, Vorlagen und Workouts auf diesem Gerät werden ersetzt.`,
    restoreConfirm: "Ersetzen",
    restored: "Backup wiederhergestellt.",
    reminder: (n: number, lastDate: string | undefined) =>
      (lastDate ? `${n} Workouts seit dem letzten Backup (${lastDate}).` : `${n} Workouts und noch kein Backup.`) +
      " Speichere eins, damit nichts verloren geht, falls das Handy die Daten der App löscht.",
    later: "Später",
  },
  app: {
    updateReady: "Neue Version verfügbar.",
    reload: "Neu laden",
    asleep: (duration: string) =>
      `Die App war ${duration} im Hintergrund, dabei fehlten Töne. Pause und Sätze sind nachgeholt. Lass die App während des Workouts vorne und den Bildschirm an.`,
    ok: "OK",
  },
  run: {
    emptyName: "Workout",
    finish: "Beenden",
    addExercise: "Übung hinzufügen",
    notes: "Notizen",
    notesPlaceholder: "Wie lief es?",
    discard: "Workout verwerfen",
    confirmDiscard: "Workout verwerfen? Nichts davon wird gespeichert.",
    discardYes: "Verwerfen",
    colPrevious: "Vorher",
    colDone: "✓",
    tick: "Satz abhaken",
    startTimer: "Timer starten",
    stopwatch: "Stoppuhr starten",
    restLabel: "Pause",
    restOf: (time: string) => `Pause ${time}`,
    skip: "Überspringen",
    interval: "Intervall",
    intervalOn: "Intervall: an",
    intervalOff: "Intervall: aus",
    exerciseMenu: "Übung",
    changeRest: (time: string) => `Pause ändern (${time})`,
    changeTime: "Zeit ändern",
    variantLocked: "Umstellen auf Wiederholungen oder Reps in Zeit geht nur, solange kein Satz dieser Übung abgehakt ist.",
    removeExercise: "Übung entfernen",
    confirmRemoveExercise: (name: string) => `„${name}“ mit den abgehakten Sätzen aus diesem Workout entfernen?`,
    noSets: "Keine Sätze.",
    missingValues: "Erst die rot markierten Werte eintragen.",
    unfinished: (n: number) => (n === 1 ? "1 Satz ist nicht abgehakt und wird nicht gespeichert." : `${n} Sätze sind nicht abgehakt und werden nicht gespeichert.`),
    finishAnyway: "Trotzdem beenden",
    back: "Zurück",
    nothingDone: "Noch kein Satz abgehakt. Das Workout wird nicht gespeichert, nur verworfen.",
    updateTemplate: (name: string) => `Vorlage „${name}“ mit den Werten von heute aktualisieren (Gewichte, Wiederholungen, Sätze)?`,
    updateYes: "Aktualisieren",
    updateNo: "Nein",
    running: "Workout läuft",
    resume: "Zurück zum Workout",
  },
  timer: {
    ready: "Bereit",
    work: "Los!",
    rest: "Pause",
    stopwatch: "Stoppuhr",
    tapHint: "Tippen: +1 Rep",
    undo: "−1",
    pause: "Pause",
    resume: "Weiter",
    done: "Fertig",
    stop: "Stopp",
    cancel: "✕ Abbrechen",
    confirmCancel: "Satz abbrechen? Zeit und gezählte Reps werden verworfen.",
    cancelYes: "Satz abbrechen",
    paused: "Pausiert",
    setOf: (name: string, n: string, of: number) => `${name} · Satz ${n} von ${of}`,
    goalReps: (n: number) => `Ziel: ${n} Reps`,
    goalTime: (time: string) => `Ziel: ${time}`,
    next: (what: string) => `Als Nächstes: ${what}`,
    nextSet: (n: string, time: string) => `Satz ${n} · ${time}`,
    stopInterval: "Intervall beenden",
  },
  sensor: {
    menu: (on: boolean) => `Reps per Sensor zählen: ${on ? "an" : "aus"}`,
    cardHint: "Reps zählt der Sensor",
    noSensor: "Dieses Gerät liefert keine Bewegungsdaten. Das geht am Android-Handy, in der App oder in Chrome.",
    startFailed: (msg: string) => `Der Sensor ließ sich nicht starten (${msg}). Reps per Tippen zählen.`,
    storeFailed: (msg: string) => `Die Sensor-Aufnahme ließ sich nicht speichern: ${msg}`,
    counting: "Sensor zählt · Handy einstecken",
    countAtEnd: "Sensor nimmt auf · gezählt wird am Ende",
    starting: "Sensor startet …",
    holdDone: "Fertig (halten)",
    colStart: "▶",
    startSet: "Satz mit Sensor zählen",
    counted: (label: string, n: number) => `Satz ${label}: ${n} ${n === 1 ? "Rep" : "Reps"} gezählt`,
    fixLess: "Eine Rep weniger",
    fixMore: "Eine Rep mehr",
  },
  summary: {
    title: "Workout beendet",
    duration: "Dauer",
    volume: "Volumen",
    sets: "Sätze",
    date: "Datum",
    setCount: (n: number) => (n === 1 ? "1 Satz" : `${n} Sätze`),
    templateUpdated: "Die Vorlage wurde aktualisiert.",
    close: "Fertig",
  },
  template: {
    titleNew: "Neue Vorlage",
    titleEdit: "Vorlage bearbeiten",
    name: "Name",
    namePlaceholder: "z. B. Sandbag AMRAP",
    notes: "Notizen",
    notesPlaceholder: "Ablauf, Hinweise …",
    addExercise: "Übung hinzufügen",
    addSet: "+ Satz",
    rest: "Pause",
    colSet: "Satz",
    colWeight: "kg",
    colReps: "Reps",
    colDuration: "Zeit",
    colTargetReps: "Ziel-Reps",
    colTargetDuration: "Ziel-Zeit",
    noTarget: "–",
    colDistance: "m",
    unknownExercise: "Unbekannte Übung",
    hiddenBadge: "ausgeblendet",
    noSets: "Keine Sätze.",
    dragHandle: "Ziehen zum Verschieben",
    removeExercise: "Übung entfernen",
    removeSet: "Satz entfernen",
    switchTo: (measure: string) => "Umstellen auf „" + measure + "“",
    copyWord: "Kopie",
    discard: "Änderungen verwerfen?",
    discardYes: "Verwerfen",
    nameMissing: "Bitte einen Namen eingeben.",
    restInvalid: "Pause zwischen 0 und 10 Minuten.",
    valuesInvalid: "Einige Werte sind ungültig (rot markiert).",
  },
  record: {
    title: "Bewegung aufzeichnen",
    titleCount: "Zähltest",
    modeRecord: "Aufnahme",
    modeCount: "Zähltest",
    introCount: "Der Sensor zählt die Reps live mit, wie im Workout. Danach gibst du ein, wie viele es wirklich waren, und siehst beides. Jeder Zähltest wird auch als Aufnahme gespeichert, und die Übung lernt daraus.",
    startCount: "Zähltest starten",
    liveCount: (n: number) => `${n} ${n === 1 ? "Rep" : "Reps"}`,
    counted: (n: number) => `Der Sensor hat ${n} ${n === 1 ? "Rep" : "Reps"} gezählt.`,
    comparison: (counted: number, reps: number) => counted === reps
      ? `Gezählt ${counted}, echt ${reps}: genau richtig.`
      : `Gezählt ${counted}, echt ${reps}: ${counted > reps ? "+" : "−"}${Math.abs(counted - reps)}.`,
    intro: "Zeichnet die Bewegungssensoren während eines Satzes auf, als Testdaten für das Zählen der Reps per Sensor. Trag das Handy so wie später im Training. Während des Satzes zählst du im Kopf mit, nicht auf dem Handy; die Zahl gibst du am Ende ein.",
    introWeb: "Zeichnet die Bewegungssensoren während eines Satzes auf, als Testdaten für das Zählen der Reps per Sensor. Trag das Handy so wie später im Training. Während des Satzes zählst du im Kopf mit, nicht auf dem Handy; die Zahl gibst du am Ende ein. Im Browser misst der Sensor seltener als in der App (etwa 60-mal pro Sekunde).",
    webData: "Die Web-App hat ihre eigenen Daten, getrennt von der Android-App: Aufnahmen von hier erscheinen nicht in der App. Exportiere sie hier.",
    noSensors: "Dieser Browser liefert keine Bewegungsdaten. Am Android-Handy in Chrome öffnen.",
    noExercises: "Keine Übungen im Glossar.",
    exercise: "Übung",
    where: "Wo ist das Handy?",
    placement: { pocket: "Hosentasche", upperArm: "Oberarm", hand: "In der Hand", floor: "Auf dem Boden", other: "Anderswo" } as Record<Placement, string>,
    placementShort: { pocket: "Tasche", upperArm: "Oberarm", hand: "Hand", floor: "Boden", other: "Anders" } as Record<Placement, string>,
    duration: "Satzdauer",
    leadIn: "Vorlauf",
    leadInHint: "Zeit zum Verstauen des Handys vor dem Satz. Die Aufnahme läuft schon im Vorlauf.",
    start: "Aufnahme starten",
    startFailed: (msg: string) => `Die Sensoren ließen sich nicht starten: ${msg}`,
    stopFailed: (msg: string) => `Die Aufnahme ging verloren: ${msg}`,
    runTitle: (name: string, where: string) => `${name} · ${where}`,
    stow: "Handy verstauen",
    measuring: "Sensoren starten …",
    rate: (hz: number) => `Sensor: ${hz} Messungen/s`,
    holdToStop: "Stopp (halten)",
    countInHead: "Reps im Kopf mitzählen. Die Zahl gibst du nach dem Satz ein.",
    confirmCancel: "Aufnahme abbrechen? Sie wird verworfen.",
    cancelYes: "Aufnahme abbrechen",
    reviewTitle: "Aufnahme prüfen",
    samples: (n: number, accelHz: number, gyroHz: number | undefined) =>
      `${n} Messwerte · Beschleunigung ${Math.round(accelHz)} Hz · ${gyroHz === undefined ? "kein Gyroskop" : `Gyroskop ${Math.round(gyroHz)} Hz`}`,
    noSamples: "Keine Sensordaten. Im Browser am PC gibt es keine Bewegungssensoren.",
    reps: "Geschaffte Reps",
    repsHint: "Wie viele Wiederholungen hast du geschafft?",
    notes: "Notizen",
    notesPlaceholder: "z. B. rechte Tasche, Display zum Bein, 20 kg",
    save: "Aufnahme speichern",
    discard: "Verwerfen",
    confirmDiscard: "Aufnahme verwerfen, ohne sie zu speichern?",
    stored: (what: string) => `Auf dem Handy gespeichert: ${what}. Weiter mit der nächsten Aufnahme.`,
    storeFailed: (msg: string) => `Speichern fehlgeschlagen: ${msg}`,
    loadFailed: (msg: string) => `Die gespeicherten Aufnahmen ließen sich nicht laden: ${msg}`,
    deleteFailed: (msg: string) => `Löschen fehlgeschlagen: ${msg}`,
    storedTitle: (n: number) => `Auf dem Handy (${n})`,
    noneStored: "Noch keine Aufnahme. Jede gespeicherte Aufnahme erscheint hier, exportiert wird alles auf einmal.",
    describe: (exercise: string, where: string, reps: number) => `${exercise} · ${where} · ${reps} ${reps === 1 ? "Rep" : "Reps"}`,
    exportHint: "Alle Aufnahmen kommen in eine Datei. Schick sie an den PC; sie kommt nach tests/fixtures/motion/. Aus Workouts bleiben je Übung nur die letzten 20 Sätze, ältere werden gelöscht: vorher exportieren.",
    exportAll: (n: number) => (n === 1 ? "Aufnahme exportieren" : `Alle ${n} exportieren`),
    exported: (name: string, n: number) => `Exportiert: ${n === 1 ? "1 Aufnahme" : `${n} Aufnahmen`} in ${name}. Sie bleiben auf dem Handy, bis du sie löschst.`,
    delete: "Löschen",
    deleteAll: "Alle löschen",
    confirmDelete: (what: string, when: string) => `Aufnahme „${what}“ vom ${when} löschen?`,
    confirmDeleteAll: (n: number) => `Alle ${n} Aufnahmen vom Handy löschen? Schon exportierte Dateien bleiben erhalten.`,
  },
  picker: {
    title: "Übungen wählen",
    create: "+ Neu",
    add: (n: number) => (n ? `${n} hinzufügen` : "Hinzufügen"),
  },
  timePicker: {
    minutes: "Min",
    seconds: "Sek",
    apply: "Übernehmen",
    clear: "Kein Ziel",
  },
  setType: { normal: "Normaler Satz", warmup: "Aufwärmsatz (W)", drop: "Dropsatz (D)", failure: "Bis zum Versagen (F)" } as Record<SetType, string>,

  equipment: (key: string) => equipment[key] ?? key,
  bodyPart: (key: BodyPart) => bodyPart[key],
  measure: (key: Measure) => measure[key],
  measureHint: (key: Measure) => measureHint[key],
  exerciseError: (key: ExerciseError) => exerciseError[key],

  exercises: {
    title: "Übungen",
    add: "+ Neu",
    search: "Suchen …",
    filterEquipment: "Ausrüstung",
    filterBodyPart: "Körperteil",
    none: "Keine Übung gefunden.",
    showHidden: "Ausgeblendete zeigen",
    hideHidden: "Ausgeblendete verbergen",
    hiddenBadge: "ausgeblendet",
  },
  exercise: {
    tabInfo: "Info",
    tabHistory: "Verlauf",
    tabCharts: "Diagramme",
    tabRecords: "Rekorde",
    comingSoon: "Kommt bald.",
    equipment: "Ausrüstung",
    bodyPart: "Körperteil",
    measure: "Messung",
    rest: "Pause",
    notes: "Notizen",
    edit: "Bearbeiten",
    delete: "Löschen",
    unhide: "Wieder einblenden",
    hiddenInfo: "Diese Übung ist ausgeblendet, weil Vorlagen oder Workouts sie benutzen.",
    confirmDelete: (name: string) => `„${name}“ löschen?`,
    confirmHide: (name: string, templates: number, sessions: number) =>
      `„${name}“ wird von ${templates} Vorlage(n) und ${sessions} Workout(s) benutzt. Sie wird nur ausgeblendet, der Verlauf bleibt.`,
    hide: "Ausblenden",
  },
  editor: {
    titleNew: "Neue Übung",
    titleEdit: "Übung bearbeiten",
    name: "Name",
    namePlaceholder: "z. B. Sandbag Clean",
    equipment: "Ausrüstung",
    ownEquipment: "Eigene …",
    ownEquipmentPlaceholder: "z. B. Steinkugel",
    bodyPart: "Körperteil",
    measure: "Messung",
    rest: "Pause nach jedem Satz",
    notes: "Notizen",
    notesPlaceholder: "Ausführung, Einstellungen …",
  },
};

/** A workout clock: m:ss, or h:mm:ss from one hour on. */
export const formatClock = (ms: number) => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const rest = formatDuration(total % 3600);
  return h ? `${h}:${rest.padStart(5, "0")}` : rest;
};

/** kg with German decimal comma and thousands separator. */
export const formatKg = (kg: number) => `${kg.toLocaleString("de-DE", { maximumFractionDigits: 2 })} kg`;

/** Seconds as m:ss. */
export const formatDuration = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, "0")}`;

/** A date as 10.10.2026. */
export const formatDate = (ms: number) => new Date(ms).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });

/** A date as "Sa., 10. Okt." with the time, for the history. */
export const formatDay = (ms: number) =>
  `${new Date(ms).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" })}, ${new Date(ms).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}`;

/** A date as 10.10., for chart axes. */
export const formatShortDate = (ms: number) => new Date(ms).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });

const num = (n: number, digits = 1) => n.toLocaleString("de-DE", { maximumFractionDigits: digits });

/** A metric or record value with its unit: 116,7 kg, 12 Reps, 0:45, 20 m, 1:15 /100 m. */
export function formatValue(unit: Unit, v: number): string {
  switch (unit) {
    case "kg": return formatKg(Math.round(v * 10) / 10);
    case "reps": return `${num(v, 0)} Reps`;
    case "sec": return formatDuration(Math.round(v));
    case "m": return `${num(v)} m`;
    case "pace": return `${formatDuration(Math.round(v))} /100 m`;
  }
}

/** A change with its sign: +2,5 kg, −3 Reps, +0:05. */
export function formatDelta(unit: Unit, d: number): string {
  const sign = d > 0 ? "+" : d < 0 ? "−" : "±";
  return sign + formatValue(unit, Math.abs(d));
}

/** One done set in a line: 100 kg × 5, 15 Reps, 20 kg · 12 Reps in 0:40, 0:45, 80 kg · 20 m in 0:15. */
export function formatSet(s: SetValues, e: Pick<Exercise, "equipment" | "measure"> | undefined): string {
  const weight = s.weightKg !== undefined && e?.equipment !== "bodyweight" ? formatKg(s.weightKg) : undefined;
  const time = s.durationSec !== undefined ? formatDuration(Math.round(s.durationSec)) : undefined;
  switch (e?.measure) {
    case "reps": return weight ? `${weight} × ${s.reps ?? "–"}` : `${s.reps ?? "–"} Reps`;
    case "timed": return [weight, `${s.reps ?? "–"} Reps${time ? ` in ${time}` : ""}`].filter(Boolean).join(" · ");
    case "hold": return [weight, time ?? "–"].filter(Boolean).join(" · ");
    case "distance": return [weight, `${s.distanceM !== undefined ? `${num(s.distanceM)} m` : "–"}${time ? ` in ${time}` : ""}`].filter(Boolean).join(" · ");
    default: return [weight, s.reps !== undefined ? `${s.reps} Reps` : undefined, time, s.distanceM !== undefined ? `${num(s.distanceM)} m` : undefined].filter(Boolean).join(" · ") || "–";
  }
}

/** A new record as a short badge: "1RM 120 kg", "6 Reps × 100 kg", "13 Reps in 0:40". */
export function formatBadge(r: Rec): string {
  if (r.kind === "repsAtWeight" && r.at !== undefined) return `${num(r.value, 0)} Reps × ${formatKg(r.at)}`;
  if (r.kind === "timedReps" && r.at !== undefined) return `${num(r.value, 0)} Reps in ${formatDuration(r.at)}`;
  if (r.kind === "maxReps") return `${num(r.value, 0)} Reps`;
  if (r.kind === "sessionReps") return `${num(r.value, 0)} Reps gesamt`;
  return `${t.progress.badge[r.kind]} ${formatValue(RECORD_UNITS[r.kind], r.value)}`;
}

/** A record's name on the Rekorde tab. */
export function recordName(r: Rec): string {
  if (r.kind === "repsAtWeight" && r.at !== undefined) return t.progress.repsAt(formatKg(r.at));
  if (r.kind === "timedReps" && r.at !== undefined) return t.progress.repsIn(formatDuration(r.at));
  return t.progress.record[r.kind];
}
